import React, { useEffect, useState } from 'react';

import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';
import { useSearchHeader } from 'frontend/src/contexts/SearchHeaderContext';

import { Image } from '@src/stories/elements/Image/Image';

// Types
import { CategoryItem } from '@src/types/search';

interface SearchCategoriesProps {
  title?: string;
  categories?: CategoryItem[];
  className?: string;
  popularCollections?: {
    title: string;
    collections: { id: string; handle: string; title: string }[];
    showWhen: 'before_search' | 'no_results' | 'both';
  };
  featuredCollection?: {
    label: string;
    link: string;
    image: string;
    showWhen?: 'before_search' | 'no_results' | 'both';
  };
  settings: any;
  onCategoryClick?: (category: CategoryItem) => void;
}

export const SearchCategories: React.FC<SearchCategoriesProps> = ({
  title = 'Collections',
  className = '',
  popularCollections,
  featuredCollection,
  settings,
  onCategoryClick
}) => {
  const [categories, setCategories] = useState<CategoryItem[] | null>(null);
  const [noResults, setNoResults] = useState(false);
  const [noQuery, setNoQuery] = useState(true);
  const [imageKey, setImageKey] = useState(0);
  const [imageHeight, setImageHeight] = useState(501); // Default height

  const { isMobile } = useResponsiveBreakpoints();

  // Helper function to calculate image height
  const calculateImageHeight = () => {
    return popularCollections?.collections?.length > 0 && shouldShowSection(popularCollections.showWhen) ? 430 : 501; // TODO: May need to adjust to check actual image height
  };

  const renderLimit = settings.max_results || 7;

  // Helper function to determine if a section should be visible
  const shouldShowSection = (showWhen: string): boolean => {
    switch (showWhen) {
      case 'before_search':
        return noQuery;
      case 'no_results':
        return noResults && !noQuery;
      case 'both':
        return noQuery || noResults;
      case 'never':
        return false;
      default:
        return false;
    }
  };

  const handleCategoriesUpdate = (event: Event) => {
    const newCategories = (event as CustomEvent).detail.categories as CategoryItem[];

    if (!newCategories) return;

    setCategories(newCategories);
  };

  const handleCategoryClick = (category: CategoryItem) => {
    if (onCategoryClick) {
      onCategoryClick(category);
    } else {
      // Default behavior: redirect to collection page
      const collectionUrl = category.url || `/collections/${category.handle}`;
      window.location.href = collectionUrl;
    }
  };

  const handleSearchStateChange = (event: Event) => {
    const { hasResults, query }: { hasResults: boolean; query: string } = (event as CustomEvent).detail || {};

    if (!hasResults) {
      setCategories([]);
    }

    if (!hasResults && !query) {
      setNoQuery(true);
      setNoResults(true);
    } else if (!hasResults && query) {
      setNoResults(true);
      setNoQuery(false);
    } else {
      setNoResults(false);
      setNoQuery(false);
    }
  };

  useEffect(() => {
    // Listen for collection data from search results
    window.addEventListener('searchrequest-categories-complete', handleCategoriesUpdate);
    window.addEventListener('search-state-changed', handleSearchStateChange);

    return () => {
      window.removeEventListener('searchrequest-categories-complete', handleCategoriesUpdate);
      window.removeEventListener('search-state-changed', handleSearchStateChange);
    };
  }, []);

  // Recalculate image height of featured category image when popularCollections changes
  useEffect(() => {
    setImageHeight(calculateImageHeight());
    setImageKey((prevKey) => prevKey + 1);
  }, [shouldShowSection(popularCollections.showWhen)]);

  // Generate spacing classes from settings
  const spacingClasses = [
    settings.margin_top ? `mt-[${settings.margin_top}px]` : '',
    settings.margin_bottom ? `mb-[${settings.margin_bottom}px]` : '',
    settings.margin_left ? `ml-[${settings.margin_left}px]` : '',
    settings.margin_right ? `mr-[${settings.margin_right}px]` : '',
    settings.padding_top ? `pt-[${settings.padding_top}px]` : '',
    settings.padding_bottom ? `pb-[${settings.padding_bottom}px]` : '',
    settings.padding_left ? `pl-[${settings.padding_left}px]` : '',
    settings.padding_right ? `pr-[${settings.padding_right}px]` : ''
  ]
    .filter(Boolean)
    .join(' ');

  if (
    (!categories || categories.length === 0) &&
    !shouldShowSection(popularCollections.showWhen) &&
    !shouldShowSection(featuredCollection.showWhen)
  )
    return null;

  return (
    <div className={`w-full ${spacingClasses} ${className}`}>
      {/* Categories Section */}
      {categories && categories.length > 0 && !noResults && (
        <>
          <p className={`mb-4 ${isMobile ? 'b7--bold' : 'b6--bold'}`}>{title}</p>

          <div className='flex flex-wrap lg:flex-col gap-4 items-start'>
            {categories.slice(0, renderLimit).map((category, index) => (
              <button
                key={category.handle || index}
                onClick={() => handleCategoryClick(category)}
                className={`block hover:underline w-auto ${isMobile ? 'b8--medium' : 'b7--medium'}`}
                title={`View ${category.title} collection`}
              >
                <span>{category.title}</span>
              </button>
            ))}
          </div>
        </>
      )}

      <div className='space-y-6'>
        {/* Popular Collections Section */}
        {popularCollections &&
          popularCollections.collections &&
          popularCollections.collections.length > 0 &&
          shouldShowSection(popularCollections.showWhen) && (
            <div>
              <p className={`mb-4 ${isMobile ? 'b7--bold' : 'b6--bold'}`}>{popularCollections.title}</p>

              <div className='flex flex-wrap lg:flex-col max-md:gap-y-2 gap-4 items-start'>
                {popularCollections.collections.map((collection) => (
                  <a
                    key={collection.id}
                    href={`/collections/${collection.handle}`}
                    className={`block hover:underline w-auto ${isMobile ? 'b8--medium' : 'b7--medium'}`}
                    title={`View ${collection.title} collection`}
                  >
                    <span>{collection.title}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

        {/* Featured Collection Section */}
        {featuredCollection &&
          featuredCollection.label &&
          featuredCollection.link &&
          shouldShowSection(featuredCollection.showWhen) && (
            <div>
              <div className=''>
                {featuredCollection.image && (
                  <div>
                    {featuredCollection.link ? (
                      <a href={featuredCollection.link}>
                        <Image
                          key={imageKey}
                          imageUrl={featuredCollection.image}
                          alt={featuredCollection.label}
                          classes='w-full mb-6'
                          width={768}
                          height={imageHeight}
                          isLazy={true}
                        />
                      </a>
                    ) : (
                      <Image
                        key={imageKey} // Use the state key to force re-render
                        imageUrl={featuredCollection.image}
                        alt={featuredCollection.label}
                        classes='w-full mb-6'
                        height={imageHeight}
                        width={768}
                        isLazy={true}
                      />
                    )}
                  </div>
                )}
                <a
                  href={featuredCollection.link || '/collections/all'}
                  className='button button-outline flex items-center justify-center w-full'
                >
                  {featuredCollection.label}
                </a>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};
